import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/20/solid';
import axios from 'axios';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { Button } from '@/components/Ui/Button';

const RequestPasswordResetPage = function () {
    const [
        t,
    ] = useTranslation('pages');

    const [
        email,
        setEmail,
    ] = useState('');

    const [
        waiting,
        setWaiting,
    ] = useState(false);

    const [
        requested,
        setRequested,
    ] = useState(false);

    const requestPasswordReset = async () => {
        setWaiting(true);

        await axios.post(`/api/auth/reset-password`, {
            email: email,
        }).then(() => {
            setRequested(true);
        }).catch(() => {
            setWaiting(false);
        });

        return false;
    };

    if (requested) {
        return (
            <div className='max-w-md'>
                <p className='mb-4'>{t('We sent a password reset link to your email address.')}</p>
                <p>{t('Please check your inbox and follow the instructions to set a new password. If you don\'t see the email, check your spam folder.')}</p>
            </div>
        );
    }

    return (
        <div className='max-w-xl text-foreground'>
            <span className='text-xl font-bold text-primary'>{t('Forgot Your Password?')}</span>
            <p className='mt-4'>{t('Enter the email address associated with your account below, and we\'ll send you a secure link to reset it.')}</p>

            <p className='font-bold my-4'>{t('What to expect:')}</p>
            <div className='bg-muted border-border border p-4 my-4 rounded-lg text-sm'>
                <ul className='list-inside list-decimal'>
                    <li className='mb-2'>{t('You\'ll receive an email from us within a couple of minutes.')}</li>
                    <li className='mb-2'>{t('Follow the "Reset Password" link in that email (it\'s valid for 30 minutes for your security).')}</li>
                    <li>{t('Create a fresh password and you\'ll be able to login again!')}</li>
                </ul>
            </div>

            <p className='text-sm'>{t('Can\'t find the email? Be sure to check your "Promotions" or "Spam" folders just in case!')}</p>
            <form className='mt-8 space-y-6' onSubmit={ (e: FormEvent) => {
                e.preventDefault();

                return requestPasswordReset();
            } }>
                <input defaultValue='true' name='remember' type='hidden' />
                <div className='-space-y-px rounded-md shadow-sm'>
                    <div>
                        <label className='sr-only' htmlFor='email'>
                            {t('Email')}
                        </label>
                        <input
                            autoComplete='email'
                            className='relative block w-full appearance-none rounded-md border border-input px-3 py-2 text-foreground placeholder-muted-foreground ring-offset-primary focus-visible:outline-none focus:z-10 focus-visible:ring-1 focus:outline-none focus-visible:ring-offset-1 sm:text-sm'
                            id='email'
                            name='email'
                            onChange={ (e) => {
                                return setEmail(e.target.value);
                            } }
                            placeholder={ t('Email') }
                            required
                            type='email'
                            value={ email }
                        />
                    </div>
                </div>

                <div className='pt-4 flex flex-col gap-y-8'>
                    <Button
                        disabled={ waiting }
                        type='submit'
                    >
                        <ArrowRightIcon aria-hidden='true' className='h-5 w-5' />
                        {t('Request password reset')}
                    </Button>

                    <NavLink
                        className='group relative flex gap-2 w-full text-sm justify-center rounded-md border border-transparent bg-transparent hover:text-primary'
                        to='/login'
                    >
                        <ArrowLeftIcon className='w-5 h-5' />
                        {t('Back to login')}
                    </NavLink>
                </div>
            </form>
        </div>
    );
};

export default RequestPasswordResetPage;
